import { useQuery } from '@tanstack/react-query';
import { Block, CoachButton, ErrorNote, Eyebrow, Loading, Screen } from '../components/primitives';
import { COACH_URL, coaching, getPublicConfig } from '../lib/api';
import { inviteLink } from '../lib/invite-link';
import { useReferralCode } from '../lib/queries';
import { copyText, shareText } from '../lib/share';

/**
 * The member's own invite link. Every account carries a referral code from
 * signup (a coach signup has no slot, and needs none for this), and the coach
 * `/signup` reads `?ref=`. Under `links_only` a link like this is the ONLY way
 * in, which is why the screen says so.
 */
export function InvitePage() {
  const referral = useReferralCode();
  const config = useQuery({
    queryKey: ['public-config'],
    queryFn: getPublicConfig,
    staleTime: 5 * 60_000,
  });

  if (referral.isPending) return <Loading />;
  if (referral.isError)
    return <ErrorNote error={referral.error} retry={() => void referral.refetch()} />;

  const link = inviteLink(COACH_URL, referral.data.shareRef);
  const linksOnly = config.data?.signupMode === 'links_only';

  const copy = async () => {
    if (!link) return;
    if ((await copyText(link)) === 'copied') coaching.event('invite_link_copied');
  };
  const share = async () => {
    if (!link) return;
    const outcome = await shareText({
      title: 'Join me on Miracles Protocol',
      text: 'I use this to plan my day and stay on track. Here is my link to join:',
      url: link,
    });
    if (outcome === 'shared') coaching.event('invite_link_shared');
    if (outcome === 'copied') coaching.event('invite_link_copied');
  };

  if (!link) {
    return (
      <Screen back="/profile" eyebrow="Invite" title="Invite a friend">
        <Block title="Your invite link is not ready yet.">
          Your account has no referral code to build one from. Ask your sponsor or the team.
        </Block>
      </Screen>
    );
  }

  return (
    <Screen
      back="/profile"
      eyebrow="Invite"
      title="Invite a friend"
      subtitle={
        linksOnly
          ? 'New members can only join through a link like yours, so this is how someone gets in.'
          : 'Anyone who joins through your link is placed under you.'
      }
    >
      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-2">Your link</Eyebrow>
        <p className="break-all border border-[rgba(16,32,26,0.22)] bg-surface px-4 py-3 font-mono text-[0.85rem] leading-relaxed text-ink">
          {link}
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <CoachButton kind="gold" onClick={() => void share()}>
            Share
          </CoachButton>
          <CoachButton onClick={() => void copy()}>Copy link</CoachButton>
        </div>
      </section>
      {referral.data.code && (
        <section className="border-b border-border px-5 py-6">
          <Eyebrow className="mb-2">Your code</Eyebrow>
          <p className="font-display text-3xl text-ink">{referral.data.code}</p>
          <p className="mt-2 text-sm leading-relaxed text-ink/70">
            The same thing in words, for anyone who would rather type it in at signup.
          </p>
        </section>
      )}
    </Screen>
  );
}
